import { useEffect, useRef, useState } from 'react';
import useDirtyContinuation from './useDirtyContinuation';

/**
 * `useDirtyPrompt({ dirty })` → `{ continueWhenClean, saveRef, discardRef, promptProps }`
 *
 * THE three-way unsaved-changes prompt for an editor that publishes its own
 * save / discard handles (`TaskEditor`'s `saveRequestRef` / `discardRequestRef`,
 * `GagDetailPanel`'s `submitRef` / `discardRef`) — `useDirtyContinuation` plus
 * the prompt's open state and its three outcomes, written once. Extracted
 * 2026-10-07 when My Tasks, the gag panel's task board and the Breakdown's
 * Timeline panel stopped refusing-and-pulsing (owner: "A, convert all three").
 *
 *  - `continueWhenClean(action)`: clean → runs now; dirty → held + prompt.
 *  - `saveRef` / `discardRef`: hand to the editor; it publishes into them.
 *  - `promptProps`: spread into `ConfirmDialog` beside `UNSAVED_PROMPT_BUTTONS`
 *    and `variant="save"` (both stay WRITTEN at the call site —
 *    `constants/saveButtonsAreConfirm.test.js` reads the variant there).
 *
 * Outcomes: Keep editing (also Escape / overlay) → 'kept', nothing runs.
 * Discard → the editor's discard, then 'discarded' (the editor goes clean, so
 * the held click runs in the hook's effect). Save → AWAITS the editor's save
 * and reports 'saved' only when it resolved true, else 'failed' — so a failed
 * save can never run the held click, even if the editor is cleaned later.
 *
 * ⚠️ Not for the order pages / GagGroupTable, whose editors raise their OWN
 * dialog from a `promptSignal` (`OrderInlineEditor`'s `collapseRequestSignal`).
 */
export default function useDirtyPrompt({ dirty }) {
  const { continueWhenClean, promptSignal, onPromptResolved } = useDirtyContinuation({ dirty });
  const saveRef = useRef(null);
  const discardRef = useRef(null);
  const [open, setOpen] = useState(false);
  // Seeded from the MOUNT value, so a remount never prompts (the order editor's rule).
  const prevSignal = useRef(promptSignal);
  useEffect(() => {
    if (promptSignal === prevSignal.current) return;
    prevSignal.current = promptSignal;
    setOpen(true);
  }, [promptSignal]);

  const onCancel = () => { setOpen(false); onPromptResolved('kept'); };
  const onAlt = () => {
    setOpen(false);
    discardRef.current?.();
    onPromptResolved('discarded');
  };
  const onConfirm = async () => {
    setOpen(false);
    const save = saveRef.current;
    const ok = save ? await save() : false;
    onPromptResolved(ok ? 'saved' : 'failed');
  };

  return { continueWhenClean, saveRef, discardRef, promptProps: { open, onCancel, onAlt, onConfirm } };
}
